import { Home, Box, Library, User, Scan } from 'lucide-react';
import { motion } from 'motion/react';
import type { Tab } from '@visionar/shared';
import { useUIStore } from '@/stores/ui-store';

const tabs: { id: Tab; icon: React.ElementType; label: string }[] = [
  { id: 'home', icon: Home, label: 'Home' },
  { id: 'ar', icon: Scan, label: 'AR' },
  { id: 'library', icon: Library, label: 'Library' },
  { id: 'profile', icon: User, label: 'Profile' },
];

export default function BottomNav() {
  const { currentTab, setCurrentTab, setARActive } = useUIStore();

  const handleTabChange = (tab: Tab) => {
    if (tab === 'ar') {
      setARActive(true);
    } else {
      setCurrentTab(tab);
    }
  };

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      className="absolute bottom-0 left-0 right-0 p-4 pb-6 safe-area-bottom z-40 pointer-events-none"
    >
      <div className="glass-dark rounded-[28px] p-1.5 flex justify-between items-center shadow-[0_8px_32px_rgba(0,0,0,0.5)] border border-white/10 pointer-events-auto">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = currentTab === tab.id;
          const isAR = tab.id === 'ar';

          return (
            <motion.button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              whileTap={{ scale: 0.9 }}
              className={`relative flex flex-col items-center justify-center flex-1 h-14 rounded-[22px] transition-colors duration-200 ${
                isActive ? 'text-white' : 'text-white/40'
              }`}
            >
              {/* Active background */}
              {isActive && !isAR && (
                <motion.div
                  layoutId="nav-indicator"
                  className="absolute inset-1 bg-white/10 rounded-[18px]"
                  transition={{ type: 'spring', bounce: 0.15, duration: 0.5 }}
                />
              )}

              {/* AR button special style - elevated pill design */}
              {isAR && (
                <motion.div
                  className="absolute left-2 right-2 top-1.5 bottom-1.5 rounded-2xl bg-gradient-to-br from-accent-blue to-accent-purple"
                  animate={{
                    boxShadow: isActive
                      ? '0 4px 20px rgba(0,212,255,0.5), 0 0 40px rgba(139,92,246,0.3)'
                      : '0 2px 12px rgba(0,212,255,0.4), 0 0 20px rgba(139,92,246,0.2)',
                  }}
                />
              )}

              {/* Icon */}
              <motion.div
                animate={{
                  scale: isActive ? 1.1 : 1,
                  y: isActive ? -1 : 0,
                }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                className="relative z-10 mt-1"
              >
                <Icon
                  size={22}
                  strokeWidth={isActive ? 2.5 : 2}
                  className={`transition-all duration-200 ${
                    isActive && !isAR ? 'drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]' : ''
                  } ${isAR ? 'text-white' : ''}`}
                />
              </motion.div>

              {/* Label */}
              <motion.span
                animate={{
                  opacity: isActive ? 1 : 0.6,
                  y: isActive ? 0 : 1,
                }}
                className={`text-[10px] mt-0.5 font-semibold relative z-10 ${
                  isAR ? 'text-white' : ''
                }`}
              >
                {tab.label}
              </motion.span>

              {/* Active dot indicator */}
              {isActive && !isAR && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -bottom-0.5 w-1 h-1 rounded-full bg-accent-blue shadow-[0_0_6px_rgba(0,212,255,0.8)]"
                />
              )}
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
}
